import FaqCard from 'components/FaqCard';
import PageTitle from 'components/PageTitle';
import RoundedButton from 'components/RoundedButton';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import MainLayout from 'components/layout/MainLayout';
import { NextSeo } from 'next-seo';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { ASSET_PREFIX } from 'utils/constants';
import { AllFAQs } from '../constants/FAQs';
import styles from './faq.module.css';
import Arrow10x20 from 'components/svg/Arrow10x20';
import { useRefArray } from 'hooks/useRefArray';
import { prepareForTransition } from 'utils/dom';
import { toNumberOrUndefined } from '@daymade/common-lib/parsing';

function getHeaderHeight() {
  const value = getComputedStyle(document.documentElement).getPropertyValue(
    '--dm2025-headerHeight',
  );
  const cleanedValue = value.trim().replace('px', '');
  return toNumberOrUndefined(cleanedValue) || 83;
}
/** How much we should scroll before the index section starts to shrink */
const PAGE_INDEX_SCROLL_AMOUNT = 100;
/** When we scroll to a faq section, we want to have some spacing between the bottom of the buttons and the section's icon */
const SCROLL_SPACING = 10;

const getFAQSchemaMainEntity = () => {
  const ungroupedFaqs = AllFAQs.flatMap((x) => x.faqs);
  return ungroupedFaqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: convertReactElementToString(faq.answer),
    },
  }));
};

const FAQSchema = () => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: getFAQSchemaMainEntity(),
      }),
    }}
  />
);

function convertReactElementToString(element: ReactElement) {
  if (!element) {
    return '';
  }
  if (typeof element == 'string') {
    return element;
  }

  if (Array.isArray(element.props?.children)) {
    return element.props.children
      .map((x: ReactElement) => convertReactElementToString(x))
      .join(' ');
  }
  return convertReactElementToString(element.props?.children);
}

const FAQ = () => {
  const [setSectionRef, sectionRefs] = useRefArray<HTMLElement>();

  return (
    <MainLayout limitItemWidths>
      <NextSeo
        title="FAQ | Win Epic Prizes | DAYMADE"
        description="Check out our FAQ to find all the answers to your questions. Such as: 'How do I know I won?' and 'What is the chance of winning' and 'Can I pick the dates for my trip/experience'?"
        openGraph={{
          title: 'FAQ | Win Epic Prizes | DAYMADE',
          description:
            "Check out our FAQ to find all the answers to your questions. Such as: 'How do I know I won?' and 'What is the chance of winning' and 'Can I pick the dates for my trip/experience'?",
        }}
      />

      <div className="aboutUsTypographyReset">
        <PageTitle
          heroBackgroundImage={
            ASSET_PREFIX + '/images/heading/QuestionMarks.svg'
          }
          rebrandDm2025
        >
          FAQ
        </PageTitle>

        <PageIndexButtons sectionRefs={sectionRefs} />

        <AboutUsModule background="white" withReducedPadding>
          <AboutUsSiteContainer>
            {AllFAQs.map((faqGrouping, i) => {
              const Icon = faqGrouping.iconComponent;
              return (
                <section
                  ref={setSectionRef(i)}
                  id={faqGrouping.shortName}
                  className={styles.faqGrouping}
                  key={i}
                >
                  <h2 className="aboutUsTypographyHeaderLarge">
                    <div className={styles.faqHeadingIcon}>
                      <Icon />
                    </div>
                    {faqGrouping.name}
                  </h2>
                  {faqGrouping.faqs.map((faq, faqIndex) => (
                    <FaqCard
                      key={faqIndex}
                      question={faq.question}
                      answer={faq.answer}
                      rebrand2025
                      withBorder
                    />
                  ))}
                </section>
              );
            })}
            <BackToTop />
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background="tealGradient">
          <AboutUsSiteContainer>
            <div className={styles.stillLost}>
              <h2 className="aboutUsTypographyHeaderXlargeOnTeal">
                Still lost?
              </h2>
              <p>Can't find the answer to your question?</p>
              <p>
                <RoundedButton
                  rebrandDm2025
                  colorStyle="OutlineDark"
                  href="/contact-us"
                >
                  Contact us
                </RoundedButton>
              </p>
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>
      </div>
      <FAQSchema />
    </MainLayout>
  );
};

interface PageIndexButtonsProps {
  sectionRefs: (HTMLElement | null)[];
}
const PageIndexButtons = (props: PageIndexButtonsProps) => {
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(0);
  const moduleRef = useRef<HTMLDivElement>(null);
  const [stickyShowing, setStickyShowing] = useState(false);
  const [stickyHiding, setStickyHiding] = useState(false);
  const lastButtonPress = useRef(new Date());

  useEffect(() => {
    let timeoutRef: ReturnType<typeof setTimeout> | undefined = undefined;
    const onScroll = () => {
      if (!timeoutRef) {
        timeoutRef = setTimeout(onScrollThrottled, 100);
      }
    };
    const onScrollThrottled = () => {
      timeoutRef = undefined;

      let inView: number | undefined = undefined;
      const scrollYWithBottomOfIndexButtons =
        window.scrollY +
        (moduleRef.current?.getBoundingClientRect().bottom || 0) +
        SCROLL_SPACING * 2;
      // Skip checking the first section
      for (let i = 1; i < props.sectionRefs.length; i++) {
        const ref = props.sectionRefs[i];
        if (ref) {
          if (
            scrollYWithBottomOfIndexButtons >= ref.offsetTop &&
            scrollYWithBottomOfIndexButtons < ref.offsetTop + ref.offsetHeight
          ) {
            inView = i;
            break;
          }
        }
      }
      setCurrentIndex(inView);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let lastScrollYRef: number | undefined;
    const onTimeout = () => {
      if (
        lastScrollYRef !== undefined &&
        new Date().getTime() - lastButtonPress.current.getTime() > 1000
      ) {
        // If we're close to the top, ensure the sticky bar is in place
        if (
          props.sectionRefs[0]?.offsetTop &&
          window.scrollY < props.sectionRefs[0]?.offsetTop
        ) {
          setStickyShowing(true);
          setStickyHiding(false);
        } else {
          const scrollYdifference = window.scrollY - lastScrollYRef;
          if (scrollYdifference < -90) {
            // moving up the page, show the sticky header
            setStickyShowing(true);
            setStickyHiding(false);
          } else if (scrollYdifference > 15) {
            // moving down the page, hide the sticky header
            setStickyShowing(false);
            setStickyHiding(true);
          }
        }
      }

      lastScrollYRef = window.scrollY;
      timeoutId = setTimeout(onTimeout, 250);
    };

    onTimeout();
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const onButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = document.getElementById(
      e.currentTarget.getAttribute('href')?.replace('#', '') || '',
    );

    if (target && moduleRef.current) {
      // Show when a button was last pressed, so that we don't hide the sticky buttons on scroll
      lastButtonPress.current = new Date();
      const bottomOfIndexContainer =
        moduleRef.current.offsetHeight + getHeaderHeight() + SCROLL_SPACING;
      window.scrollTo({
        top: target.offsetTop - bottomOfIndexContainer,
        behavior: 'smooth',
      });
      setStickyHiding(true);
    }
  };

  return (
    <div
      ref={moduleRef}
      className={
        styles.pageIndexButtonsModule +
        ' ' +
        (stickyShowing ? styles.withStickyButtons : '') +
        (stickyHiding ? styles.withoutStickyButtons : '')
      }
    >
      <AboutUsSiteContainer>
        <ul className={styles.faqButtonContainer}>
          {AllFAQs.map(({ shortName, iconComponent: IconComponent }, index) =>
            index == 0 ? /* Skip the 'popular' icon */ undefined : (
              <li key={index}>
                <a
                  onClick={onButtonClick}
                  href={'#' + shortName}
                  className={
                    styles.faqButton +
                    (index == currentIndex
                      ? ' ' + styles.faqButtonSelected
                      : '')
                  }
                >
                  <span className={styles.faqButtonIcon}>
                    <IconComponent />
                  </span>
                  {shortName}
                </a>
              </li>
            ),
          )}
        </ul>
      </AboutUsSiteContainer>
    </div>
  );
};

const BackToTop = () => {
  return (
    <div className={styles.backToTopContainer}>
      <a href="#">
        <span className={styles.backToTopIcon}>
          <Arrow10x20 />
        </span>
        Back to top
      </a>
    </div>
  );
};

export default FAQ;
